/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';
import type { AuthState } from '../hooks/useAuth';
import type { UserProfile } from '../services/authService';

interface AuthContextType extends AuthState {
    fetchUserProfile: (force?: boolean) => Promise<UserProfile | null>;
    sendSignInLink: (email: string) => Promise<boolean>;
    signOut: () => Promise<void>;
    signUpWithEmailAndPassword: (email: string, password: string) => Promise<User>;
    signInWithEmailAndPassword: (email: string, password: string) => Promise<User>;
    signInWithGoogle: () => Promise<User>;
    sendPasswordResetEmail: (email: string) => Promise<boolean>;
    checkUserExists: (email: string) => Promise<boolean>;
    checkUserProviders: (email: string) => Promise<{ exists: boolean; providers: string[] }>;
    getGoogleCredential: () => Promise<{ credential: any; email: string; user: any }>;
    linkGoogleToAccount: (email: string, password: string, googleCredential: any) => Promise<any>;
    signInWithGoogleCredential: (credential: any) => Promise<any>;
    getCurrentUser: () => User | null;
    fetchSignInMethods: (email: string) => Promise<string[]>;
    setPasswordForCurrentUser: (password: string) => Promise<void>;
    sendEmailVerification: () => Promise<void>;
    requestVerificationEmail: () => Promise<{ cooldown_seconds?: number; remaining_today?: number }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

// Memoize the provider to prevent unnecessary re-renders
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const auth = useAuth();

    return (
        <AuthContext.Provider value={auth}>
            {children}
        </AuthContext.Provider>
    );
};

// Stabilize the hook export to prevent Fast Refresh issues
export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};
